import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, first, tap } from 'rxjs/operators';
import { environment } from '@app/../environments/environment';

export interface BillingState {
  /** ok - nothing owed, grace - trial ended unpaid, locked - grace is over */
  state: 'ok' | 'grace' | 'locked';
  days_left: number;
  trial_ends_at: string | null;
  grace_ends_at: string | null;
  can_manage_billing: boolean;
  /** False when no Stripe customer is linked, so there is no portal to open */
  has_billing_account: boolean;
  support_email: string;
}

const UNRESTRICTED: BillingState = {
  state: 'ok',
  days_left: 0,
  trial_ends_at: null,
  grace_ends_at: null,
  can_manage_billing: false,
  has_billing_account: false,
  support_email: ''
};

@Injectable({
  providedIn: 'root'
})
export class BillingService {
  private endpoint: string = environment.apiUrl + '/v2/customer/stripe';
  private stateSubject: BehaviorSubject<BillingState | null> = new BehaviorSubject<BillingState | null>(null);
  public state$: Observable<BillingState | null> = this.stateSubject.asObservable();

  constructor(private http: HttpClient) { }

  /**
   * A failed lookup always resolves to unrestricted access - nobody should be
   * locked out of the app because this call did not come back.
   */
  public loadState(): Observable<BillingState> {
    return this.http.get<BillingState>(`${this.endpoint}/billing-state`).pipe(
      first(),
      tap(state => this.stateSubject.next(state)),
      catchError(() => {
        this.stateSubject.next(UNRESTRICTED);
        return of(UNRESTRICTED);
      })
    );
  }

  /**
   * Cached for the session, the state only changes when a payment goes through
   * and that lands the user back on a fresh page load anyway.
   */
  public ensureState(): Observable<BillingState> {
    const current = this.stateSubject.getValue();
    return current ? of(current) : this.loadState();
  }

  /**
   * Stripe portal links are single use, so a session is created per click
   * rather than stored anywhere.
   */
  public createPortalSession(): Observable<{ url: string }> {
    return this.http.post<{ url: string }>(`${this.endpoint}/billing-portal`, {}).pipe(first());
  }
}
